import { type FC, useMemo, type RefObject } from 'react';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { registerHOTModules } from '@/support/hot';
import { ScoringScaleEditor } from '@/components/UI/SpreadSheet/editors/ScoringScaleEditor';
import { buildColumns, buildCellsCallback, buildAfterRendererCallback } from './BulkEditObservationTypes.utils';

import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { ObservationTypeRow, ValidationData } from '@/utils/observationTypeValidation';

registerHOTModules();
ScoringScaleEditor.register();

export interface BulkEditObservationTypesTableProps {
  data: Array<ObservationTypeRow>;
  innerRef: RefObject<HotTableRef | null>;
  observationTypes: Array<TeamGlossaryV1>;
  validationData: ValidationData | null;
  setInvalid: (invalid: boolean) => void;
}

const BulkEditObservationTypesTable: FC<BulkEditObservationTypesTableProps> = ({
  data,
  innerRef,
  observationTypes,
  validationData,
  setInvalid,
}) => {
  const columns = useMemo(
    () =>
      buildColumns({
        validationData,
        observationTypes,
        hotTableRef: innerRef,
      }),
    [validationData, observationTypes, innerRef]
  );

  const settings = useMemo<SpreadsheetSettings>(
    () => ({
      columns,
      colHeaders: ['Observation type', 'Label', 'Scoring scale', 'Grading information'],
      rowHeaders: true,
      manualColumnResize: true,
      contextMenu: false as const,
      outsideClickDeselects: false,
      height: 600,
      rowHeights: 50,
      className: 'htMiddle',
      stretchH: 'all' as const,
    }),
    [columns]
  );

  const cellsCallback = useMemo(
    () => buildCellsCallback(observationTypes, validationData),
    [observationTypes, validationData]
  );

  const afterRendererCallback = useMemo(() => buildAfterRendererCallback(observationTypes), [observationTypes]);

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles" data-testid="observation-types-spreadsheet">
      <Spreadsheet
        data={data as unknown as Array<Record<string, unknown>>}
        settings={settings}
        innerRef={innerRef as RefObject<HotTableRef>}
        addCol={false}
        maxRows={data.length}
        setInvalid={setInvalid}
        modalView={true}
        fillHandle={false}
        cells={cellsCallback}
        customAfterRenderer={afterRendererCallback}
        testPrefix="bulk-edit-observation-types"
      />
    </div>
  );
};

export default BulkEditObservationTypesTable;
